import React, { useEffect, useMemo, useRef } from 'react';
import NavigationHeader from '../../components/navigation/NavigationHeader';
import MosaicTable from '../../components/commos/MosaicTable';
import ProcessingStatus from '../../components/commos/ProcessingStatus';
import { showAlert, useAlertStore, useCombosStore } from '../../stores';
import Loading from '../../features/sso-auth/components/Loading';
import { useParams, useNavigate } from 'react-router-dom';
import folder from '@/assets/images/folder.png';

const CombosPage: React.FC = () => {
  const {
    processedCombos,
    getProcessedCombos,
    getProcessedCombosById,
    processedCombosById,
    loading,
    loadingDownload,
    updateProcessedCombos,
    updateProcessedCombosById,
    downloadGroupedCombos,
    bulkJobStatus,
    getBulkJobStatus,
  } = useCombosStore();

  const { id, type } = useParams();
  const navigate = useNavigate();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const showAlert = useAlertStore(state => state.showAlert);

  const flatCombos = useMemo(() => processedCombos.list.map((combo) => ({
    id: combo.id,
    thumbnail: folder,
    start_date: combo.created_at,
    type: combo.manual_combos_count === 0 ? 'bus' : 'manual',
    name: combo.name
  })), [processedCombos]);

  const flatCombosById = useMemo(() => processedCombosById.list.map((combo) => ({
    ...combo,
    thumbnail: combo.data.thumbnail_url,
    start_date: combo.data.start_date,
    combo_id: combo.data.combo_id
  })), [processedCombosById]);

  const handleItemClick = <T,>(item: T) => {
    const { id, type } = item as { id: number | string; type: string };
    void navigate(`/combos/table/${id}/${type}`);
  }

  const handleDownload = async<T extends { id: string | number }>(items?: T[]) => {
    const response = await downloadGroupedCombos(items?.map(item => `${item.id}`) || [], !id, type === 'manual');
    if (response.success) {
      showAlert({
        text: 'Descarga exitosa',
        message: response.message,
        status: 'success'
      });
    }
    else {
      showAlert({
        text: 'Error al descargar combos',
        message: response.message,
        status: 'error'
      });
    }
  }

  const handleDownloadAll = () => {
    if (id) {
      return handleDownload(flatCombosById);
    }
    return handleDownload(flatCombos);
  }

  useEffect(() => {
    if (!id) void getProcessedCombos();
  }, [getProcessedCombos, processedCombos.currentPage, processedCombos.itemsPerPage, id]);

  useEffect(() => {
    if (id && type) {
      void getProcessedCombosById(
        Number(id),
        type as 'manual' | 'bus',
        {
          page: processedCombosById.currentPage,
          page_size: processedCombosById.itemsPerPage,
        }
      );
    }
  }, [getProcessedCombosById, id, processedCombosById.currentPage, processedCombosById.itemsPerPage, type]);

  // Obtener estado del proceso de imágenes cuando estamos en rutas específicas
  useEffect(() => {
    if (id && (type === 'bus' || type === 'manual')) {
      void getBulkJobStatus(Number(id));
    }
  }, [id, type, getBulkJobStatus]);

  // Configurar recarga automática cada 15 segundos si el proceso no está completo
  useEffect(() => {
    if (id && (type === 'bus' || type === 'manual') && bulkJobStatus) {
      const shouldReload = !bulkJobStatus.is_complete || !bulkJobStatus.completed_at;

      if (shouldReload) {
        // Limpiar intervalo anterior si existe
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }

        // Configurar nuevo intervalo
        intervalRef.current = setInterval(() => {
          void getBulkJobStatus(Number(id));
          void getProcessedCombosById(
            Number(id),
            type as 'manual' | 'bus',
            {
              page: processedCombosById.currentPage,
              page_size: processedCombosById.itemsPerPage,
            }
          );
        }, 15000);
      } else {
        // Limpiar intervalo si el proceso está completo
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }

    // Cleanup al desmontar o cuando cambien las dependencias
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [id, type, bulkJobStatus, getBulkJobStatus, getProcessedCombosById, processedCombosById.currentPage, processedCombosById.itemsPerPage]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-sm w-full space-y-8 bg-white p-8 rounded-3xl shadow-lg">
          <Loading message="Esto solo va tomar un momento, estamos cargando las reglas." />
        </div>
      </div>
    );
  }

  if (loadingDownload) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-sm w-full space-y-8 bg-white p-8 rounded-3xl shadow-lg">
          <Loading message="Esto solo va tomar un momento, estamos procesando los archivos a descargar." />
        </div>
      </div>
    );
  }
  return (
    <>
      <NavigationHeader
        title="Combos creados"
        description="Revisa los combos de productos para promociones en BEES que se han generado."
        backLink={!id ? '/' : '/combos/table'}
      />

      {id && (type === 'bus' || type === 'manual') && bulkJobStatus && (
        <ProcessingStatus
          processedImages={bulkJobStatus.processed_images}
          totalImages={bulkJobStatus.total_images}
          progressPercentage={bulkJobStatus.progress_percentage}
          isComplete={bulkJobStatus.is_complete}
          completedAt={bulkJobStatus.completed_at}
        />
      )}

      {
        !id ? (
          <MosaicTable
            title="Listado de Combos"
            emptyMessage='No hay combos disponibles'
            searchPlaceholder='Buscar combo'
            items={flatCombos}
            onItemClick={handleItemClick}
            onPageChange={(currentPage) => updateProcessedCombos({ currentPage })}
            onItemsPerPageChange={(itemsPerPage) => updateProcessedCombos({ itemsPerPage })}
            currentPage={processedCombos.currentPage}
            itemsPerPage={processedCombos.itemsPerPage}
            totalItems={processedCombos.totalItems}
            labels={{
              id: "",
              start_date: "Vigencia del Combo"
            }}
            onDownloadSelection={handleDownload}
            onDownloadAll={handleDownloadAll}
          />
        ) : (
          <MosaicTable
            title="Listado de Combos"
            emptyMessage='No hay combos disponibles'
            searchPlaceholder='Buscar combo'
            items={flatCombosById}
            onPageChange={(currentPage) => updateProcessedCombosById({ currentPage })}
            onItemsPerPageChange={(itemsPerPage) => updateProcessedCombosById({ itemsPerPage })}
            currentPage={processedCombosById.currentPage}
            itemsPerPage={processedCombosById.itemsPerPage}
            totalItems={processedCombosById.totalItems}
            labels={{
              id: "ID Combo",
              start_date: "Vigencia del Combo"
            }}
            onDownloadSelection={handleDownload}
            onDownloadAll={handleDownloadAll}
          />
        )
      }
    </>
  );
};

export default CombosPage;